/**
 * Notification Service
 * Handles real-time notifications, user preferences, and notification management
 */

import { supabase } from '../lib/supabase';

/**
 * Subscribe to real-time notifications for the current user
 * @param {string} userId - User ID to subscribe to
 * @param {Function} onNotification - Callback function when new notification arrives
 * @returns {Object} Subscription channel
 */
export function subscribeToNotifications(userId, onNotification) {
  const channel = supabase?.channel(`notifications_${userId}`)?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        const notification = convertNotificationFromDb(payload?.new);
        onNotification(notification);
      }
    )?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        const notification = convertNotificationFromDb(payload?.new);
        onNotification(notification);
      }
    )?.subscribe();

  return channel;
}

/**
 * Unsubscribe from notifications
 * @param {Object} channel - Subscription channel to unsubscribe
 */
export function unsubscribeFromNotifications(channel) {
  if (channel) {
    supabase?.removeChannel(channel);
  }
}

/**
 * Get all notifications for the current user
 * @param {string} userId - User ID
 * @param {Object} options - Query options (status, limit, offset)
 * @returns {Promise<Array>} Array of notifications
 */
export async function getNotifications(userId, options = {}) {
  try {
    const { status, limit = 50, offset = 0 } = options;

    let query = supabase?.from('notifications')?.select('*', { count: 'exact' })?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (status) {
      query = query?.eq('status', status);
    }

    query = query?.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      notifications: data?.map(convertNotificationFromDb) || [],
      total: count || 0
    };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return { notifications: [], total: 0 };
  }
}

/**
 * Get unread notification count
 * @param {string} userId - User ID
 * @returns {Promise<number>} Count of unread notifications
 */
export async function getUnreadCount(userId) {
  try {
    const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('status', 'unread');

    if (error) throw error;

    return count || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
}

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} Updated notification
 */
export async function markAsRead(notificationId) {
  try {
    const { data, error } = await supabase?.from('notifications')?.update({ 
        status: 'read',
        read_at: new Date()?.toISOString()
      })?.eq('id', notificationId)?.select()?.single();

    if (error) throw error;

    return convertNotificationFromDb(data);
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return null;
  }
}

/**
 * Mark all notifications as read
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} Success status
 */
export async function markAllAsRead(userId) {
  try {
    const { error } = await supabase?.from('notifications')?.update({ 
        status: 'read',
        read_at: new Date()?.toISOString()
      })?.eq('user_id', userId)?.eq('status', 'unread');

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return false;
  }
}

/**
 * Archive notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<boolean>} Success status
 */
export async function archiveNotification(notificationId) {
  try {
    const { error } = await supabase?.from('notifications')?.update({ 
        status: 'archived',
        archived_at: new Date()?.toISOString()
      })?.eq('id', notificationId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error archiving notification:', error);
    return false;
  }
}

/**
 * Delete notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteNotification(notificationId) {
  try {
    const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error deleting notification:', error);
    return false;
  }
}

/**
 * Get user notification preferences
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User notification preferences
 */
export async function getUserPreferences(userId) {
  try {
    const { data, error } = await supabase?.from('user_notification_preferences')?.select('*')?.eq('user_id', userId)?.maybeSingle();

    if (error) throw error;

    if (!data) {
      // Create default preferences if none exist
      return await createDefaultPreferences(userId);
    }

    return convertPreferencesFromDb(data);
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    return null;
  }
}

/**
 * Create default notification preferences for user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Created preferences
 */
export async function createDefaultPreferences(userId) {
  try {
    const { data, error } = await supabase?.from('user_notification_preferences')?.insert({
        user_id: userId,
        email_notifications: true,
        push_notifications: true,
        sms_notifications: false,
        booking_confirmations: true,
        payment_alerts: true,
        trip_reminders: true
      })?.select()?.single();

    if (error) throw error;

    return convertPreferencesFromDb(data);
  } catch (error) {
    console.error('Error creating default preferences:', error);
    return null;
  }
}

/**
 * Update user notification preferences
 * @param {string} userId - User ID
 * @param {Object} preferences - Preferences to update
 * @returns {Promise<Object>} Updated preferences
 */
export async function updateUserPreferences(userId, preferences) {
  try {
    const dbPreferences = convertPreferencesToDb(preferences);

    const { data, error } = await supabase?.from('user_notification_preferences')?.update(dbPreferences)?.eq('user_id', userId)?.select()?.single();

    if (error) throw error;

    return convertPreferencesFromDb(data);
  } catch (error) {
    console.error('Error updating user preferences:', error);
    return null;
  }
}

/**
 * Convert notification from database format (snake_case) to app format (camelCase)
 */
function convertNotificationFromDb(notification) {
  if (!notification) return null;

  return {
    id: notification?.id,
    userId: notification?.user_id,
    type: notification?.type,
    title: notification?.title,
    message: notification?.message,
    status: notification?.status,
    relatedId: notification?.related_id,
    relatedType: notification?.related_type,
    metadata: notification?.metadata,
    createdAt: notification?.created_at,
    readAt: notification?.read_at,
    archivedAt: notification?.archived_at
  };
}

/**
 * Convert preferences from database format (snake_case) to app format (camelCase)
 */
function convertPreferencesFromDb(preferences) {
  if (!preferences) return null;

  return {
    id: preferences?.id,
    userId: preferences?.user_id,
    emailNotifications: preferences?.email_notifications,
    pushNotifications: preferences?.push_notifications,
    smsNotifications: preferences?.sms_notifications,
    bookingConfirmations: preferences?.booking_confirmations,
    paymentAlerts: preferences?.payment_alerts,
    tripReminders: preferences?.trip_reminders,
    createdAt: preferences?.created_at,
    updatedAt: preferences?.updated_at
  };
}

/**
 * Convert preferences from app format (camelCase) to database format (snake_case)
 */
function convertPreferencesToDb(preferences) {
  const dbPreferences = {};

  if (preferences?.emailNotifications !== undefined) {
    dbPreferences.email_notifications = preferences?.emailNotifications;
  }
  if (preferences?.pushNotifications !== undefined) {
    dbPreferences.push_notifications = preferences?.pushNotifications;
  }
  if (preferences?.smsNotifications !== undefined) {
    dbPreferences.sms_notifications = preferences?.smsNotifications;
  }
  if (preferences?.bookingConfirmations !== undefined) {
    dbPreferences.booking_confirmations = preferences?.bookingConfirmations;
  }
  if (preferences?.paymentAlerts !== undefined) {
    dbPreferences.payment_alerts = preferences?.paymentAlerts;
  }
  if (preferences?.tripReminders !== undefined) {
    dbPreferences.trip_reminders = preferences?.tripReminders;
  }

  return dbPreferences;
}